import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ConnectWixPaymentsAccountBase44ApplicationErrors,
  ConnectWixPaymentsAccountBase44Options,
  ConnectWixPaymentsAccountBase44Response,
  ConnectWixPaymentsAccountResponse,
  SetupBase44SiteOptions,
  SetupBase44SiteResponse,
  WixPaymentsAccount,
  connectWixPaymentsAccount as universalConnectWixPaymentsAccount,
  connectWixPaymentsAccountBase44 as universalConnectWixPaymentsAccountBase44,
  setupBase44Site as universalSetupBase44Site,
} from './payments-mcp-v1-wix-payments-account-wix-payments-account.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

export function connectWixPaymentsAccount(
  httpClient: HttpClient
): ConnectWixPaymentsAccountSignature {
  return (
    account: NonNullablePaths<
      WixPaymentsAccount,
      `firstName` | `lastName` | `tosAccepted`,
      2
    >
  ) =>
    universalConnectWixPaymentsAccount(
      account,
      // @ts-ignore
      { httpClient }
    );
}

interface ConnectWixPaymentsAccountSignature {
  /**
   * Connects a Wix Payments account to the site.
   *
   *
   * The call fails if:
   * - The site's location
   * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/business-tools/locations/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/locations/introduction))
   * is outside the USA.
   * - A Wix Payments account is already connected to the site.
   * - You don't specify `{"tosAccepted": true}`.
   *
   * The connected Wix Payments account has the following limitations, which site
   * owners can manage in their dashboard:
   *
   * - By default, customers can only pay via credit card, Apple Pay, or Google Pay.
   * - To receive payments, site owners must complete onboarding to their Wix
   * Payments account in the site's dashboard.
   * @param - Wix Payments account to connect to the site.
   */
  (
    account: NonNullablePaths<
      WixPaymentsAccount,
      `firstName` | `lastName` | `tosAccepted`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ConnectWixPaymentsAccountResponse,
      `account.termsOfServiceAcceptance.ip`,
      4
    >
  >;
}

export function connectWixPaymentsAccountBase44(
  httpClient: HttpClient
): ConnectWixPaymentsAccountBase44Signature {
  return (
    account: NonNullablePaths<
      WixPaymentsAccount,
      `email` | `firstName` | `lastName` | `termsOfServiceAcceptance`,
      2
    >,
    options?: ConnectWixPaymentsAccountBase44Options
  ) =>
    universalConnectWixPaymentsAccountBase44(
      account,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ConnectWixPaymentsAccountBase44Signature {
  /**
   * Connects a Wix Payments account to the site.
   *
   *
   * The call fails if:
   * - The site's location
   * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/business-tools/locations/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/locations/introduction))
   * is outside the USA.
   * - A Wix Payments account is already connected to the site.
   * - You don't specify `{"tosAccepted": true}`.
   *
   * The connected Wix Payments account has the following limitations, which site
   * owners can manage in their dashboard:
   *
   * - By default, customers can only pay via credit card, Apple Pay, or Google Pay.
   * - To receive payments, site owners must complete onboarding to their Wix
   * Payments account in the site's dashboard.
   * @param - Wix Payments account to connect to the site.
   */
  (
    account: NonNullablePaths<
      WixPaymentsAccount,
      `email` | `firstName` | `lastName` | `termsOfServiceAcceptance`,
      2
    >,
    options?: ConnectWixPaymentsAccountBase44Options
  ): Promise<
    NonNullablePaths<
      ConnectWixPaymentsAccountBase44Response,
      | `account.termsOfServiceAcceptance.ip`
      | `base44Metadata.siteId`
      | `base44Metadata.webhookPublicKey`
      | `base44Metadata.appSecret`
      | `base44Metadata.apiKey`,
      4
    > & {
      __applicationErrorsType?: ConnectWixPaymentsAccountBase44ApplicationErrors;
    }
  >;
}

export function setupBase44Site(
  httpClient: HttpClient
): SetupBase44SiteSignature {
  return (options?: SetupBase44SiteOptions) =>
    universalSetupBase44Site(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetupBase44SiteSignature {
  /**
   * Sets up a Base44 site with an API key, without connecting a Wix Payments account.
   *
   * This endpoint:
   * - Creates or publishes the metasite
   * - Installs required apps
   * - Adds the "accept_payments" premium feature
   * - Creates an API key for the site
   * - Persists the Base44 app record
   *
   * Use `ConnectWixPaymentsAccountBase44` to also connect a Wix Payments account.
   */
  (options?: SetupBase44SiteOptions): Promise<
    NonNullablePaths<SetupBase44SiteResponse, `siteId` | `apiKey`, 2>
  >;
}

export {
  Base44AppData,
  Base44Metadata,
  ConnectWixPaymentsAccountBase44Options,
  ConnectWixPaymentsAccountBase44Request,
  ConnectWixPaymentsAccountBase44Response,
  ConnectWixPaymentsAccountRequest,
  ConnectWixPaymentsAccountResponse,
  SetupBase44SiteOptions,
  SetupBase44SiteRequest,
  SetupBase44SiteResponse,
  TermsOfServiceAcceptance,
  WixPaymentsAccount,
} from './payments-mcp-v1-wix-payments-account-wix-payments-account.universal.js';
